import React, { useEffect, useState, useRef, useCallback } from "react";
import { Navigate, NavLink, Routes, Route, useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import {
  Save, Trash2, Plus, LogOut,
  Image as ImageIcon, Newspaper, Hammer, DollarSign, Info, Phone, Star, Mail, FileText, Users,
  Upload, CheckCircle2, Eye, Calendar, ArrowLeft, Menu, X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const { user, logout } = useAuth();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  if (user === null) return <div className="min-h-screen bg-[#2A2A2A]"/>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace/>;

  const sections = [
    { to: "slides", label: "Slajdy", icon: ImageIcon },
    { to: "news", label: "Aktualności", icon: Newspaper },
    { to: "workshops", label: "Warsztaty", icon: Hammer },
    { to: "access", label: "Cennik", icon: DollarSign },
    { to: "about", label: "O nas", icon: Info },
    { to: "contact", label: "Kontakt", icon: Phone },
    { to: "sponsors", label: "Sponsorzy", icon: Star },
    { to: "documents", label: "Regulamin/Pliki", icon: FileText },
    { to: "signups", label: "Zgłoszenia", icon: Mail },
    { to: "participants", label: "Uczestnicy / Wejścia", icon: Users },
  ];

  // For mobile, we only show signups and participants
  const mobileSections = sections.filter(s => ["signups", "participants"].includes(s.to));

  const NavContent = ({ mobile = false }) => (
    <>
      <div className="mb-8 hidden md:block">
        <img src="/logo.png" alt="Śląski Maker Space" className="h-16 w-auto mb-3"/>
        <div className="font-mono-acc text-[10px] text-[#FFCC00] uppercase">Admin Panel</div>
      </div>
      <nav className="flex-1 space-y-1">
        {(mobile ? mobileSections : sections).map((s) => (
          <NavLink
            key={s.to}
            to={`/admin/${s.to}`}
            onClick={() => setSidebarOpen(false)}
            data-testid={`admin-nav-${s.to}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 font-mono-acc text-xs uppercase transition-colors ${
                isActive ? "bg-[#FFCC00] text-[#2A2A2A]" : "text-white/70 hover:bg-[#2A2A2A]"
              }`
            }
          >
            <s.icon size={16}/> {s.label}
          </NavLink>
        ))}
      </nav>
      <button
        onClick={logout}
        data-testid="admin-logout"
        className="mt-4 flex items-center gap-2 px-4 py-3 border border-[#3a3a3a] text-white/70 hover:border-[#FFCC00] hover:text-[#FFCC00] font-mono-acc text-xs uppercase"
      >
        <LogOut size={16}/> Wyloguj
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-[#2A2A2A] flex flex-col md:flex-row" data-testid="admin-dashboard">
      {/* Mobile Header */}
      <header className="md:hidden bg-[#1F1F1F] border-b border-[#3a3a3a] p-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="SMS" className="h-8 w-auto"/>
          <span className="font-mono-acc text-[10px] text-[#FFCC00] uppercase">Admin</span>
        </div>
        <button 
          onClick={() => setSidebarOpen(!isSidebarOpen)}
          className="text-white p-1"
        >
          {isSidebarOpen ? <X size={24}/> : <Menu size={24}/>}
        </button>
      </header>

      {/* Desktop Sidebar */}
      <aside className="w-64 bg-[#1F1F1F] border-r border-[#3a3a3a] p-6 hidden md:flex flex-col sticky top-0 h-screen">
        <NavContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/80" onClick={() => setSidebarOpen(false)}></div>
          <aside className="relative w-64 bg-[#1F1F1F] h-full p-6 flex flex-col shadow-xl">
            <div className="mb-8 flex items-center justify-between">
              <img src="/logo.png" alt="SMS" className="h-12 w-auto"/>
              <button onClick={() => setSidebarOpen(false)} className="text-white/50"><X size={20}/></button>
            </div>
            <NavContent mobile={true} />
          </aside>
        </div>
      )}

      <main className="flex-1 p-4 md:p-10 overflow-auto">
        <Routes>
          <Route index element={<DashHome/>}/>
          <Route path="slides" element={<CrudList endpoint="/slides" fields={[
            { key: "title", label: "Tytuł" },
            { key: "subtitle", label: "Podtytuł" },
            { key: "image_url", label: "URL obrazu", upload: true },
            { key: "font_size", label: "Rozmiar czcionki hasła", type: "select", options: [
              { value: "standard", label: "Standardowy (B. duży)" },
              { value: "small", label: "Pomniejszony" }
            ] },
            { key: "order", label: "Kolejność", type: "number" },
          ]}/>}/>
          <Route path="news" element={<NewsEditor/>}/>
          <Route path="workshops" element={<CrudList endpoint="/workshops" fields={[
            { key: "title", label: "Tytuł" },
            { key: "image_url", label: "URL obrazu", upload: true },
            { key: "short_desc", label: "Skrót" },
            { key: "description", label: "Opis", type: "textarea" },
            { key: "duration", label: "Czas trwania" },
            { key: "level", label: "Poziom" },
            { key: "cost", label: "Koszt" },
            { key: "age", label: "Wiek" },
            { key: "order", label: "Kolejność wyświetlania", type: "number" },
          ]}/>}/>
          <Route path="sponsors" element={<CrudList endpoint="/sponsors" fields={[
            { key: "name", label: "Nazwa" },
            { key: "image_url", label: "URL logo", upload: true },
          ]}/>}/>
          <Route path="access" element={<AccessEditor/>}/>
          <Route path="about" element={<AboutEditor/>}/>
          <Route path="contact" element={<ContactEditor/>}/>
          <Route path="documents" element={<DocumentsEditor/>}/>
          <Route path="signups" element={<SignupsList/>}/>
          <Route path="participants" element={<ParticipantsAdmin/>}/>
          <Route path="participants/:id" element={<AdminParticipantDetail/>}/>
        </Routes>
      </main>
    </div>
  );
}

function DashHome() {
  return (
    <div>
      <div className="kicker mb-3">Witaj</div>
      <h1 className="font-display text-white text-3xl md:text-5xl uppercase mb-4">Panel administracyjny</h1>
      <p className="text-white/65 max-w-2xl text-sm md:text-base">
        Zarządzaj zawartością strony Śląski Maker Space — slajdy, aktualności, warsztaty, cennik, galeria,
        kontakt, sponsorzy oraz zgłoszenia z formularza zapisów. Wybierz sekcję z lewego menu.
      </p>
    </div>
  );
}

function useAuthHeaders() {
  const { token } = useAuth();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/* ---------- Image Upload Field ---------- */
function ImageUrlField({ value, onChange, headers }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      onChange(`${BACKEND_URL}${data.url}`);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Błąd uploadu");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="flex gap-2">
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="flex-1 bg-[#2A2A2A] border border-[#3a3a3a] text-white px-3 py-2 outline-none focus:border-[#FFCC00]"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="bg-[#FFCC00] text-[#2A2A2A] font-mono-acc text-[10px] uppercase px-4 inline-flex items-center gap-1.5 btn-brutal disabled:opacity-50"
        >
          <Upload size={14}/> {uploading ? "..." : "Plik"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden"/>
      </div>
      {err && <div className="text-red-400 text-xs mt-1">{err}</div>}
      {value && (
        <img src={value} alt="" className="mt-2 max-h-32 object-contain border border-[#3a3a3a] bg-[#2A2A2A]"/>
      )}
    </div>
  );
}

/* ---------- Generic CRUD list ---------- */
function CrudList({ endpoint, fields }) {
  const headers = useAuthHeaders();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});

  const reload = () => api.get(endpoint).then((r) => setItems(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [endpoint]);

  const startNew = () => {
    setEditing("new");
    const empty = Object.fromEntries(fields.map((f) => [f.key, f.type === "number" ? 0 : ""]));
    setDraft(empty);
  };

  const startEdit = (it) => { setEditing(it.id); setDraft({ ...it }); };

  const save = async () => {
    if (editing === "new") await api.post(endpoint, draft, { headers });
    else await api.put(`${endpoint}/${editing}`, draft, { headers });
    setEditing(null);
    reload();
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć?")) return;
    await api.delete(`${endpoint}/${id}`, { headers });
    reload();
  };

  return (
    <div data-testid={`crud-${endpoint.replace("/", "")}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-white text-3xl uppercase">{endpoint.slice(1)}</h2>
        <button onClick={startNew} className="bg-[#FFCC00] text-[#2A2A2A] font-display text-xs px-5 py-3 btn-brutal inline-flex items-center gap-2" data-testid="crud-new-btn">
          <Plus size={14}/> NOWY
        </button>
      </div>

      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="bg-[#1F1F1F] border border-[#3a3a3a] p-4 flex items-center gap-4">
            {it.image_url && <img src={it.image_url} alt="" className="w-20 h-14 object-cover"/>}
            <div className="flex-1 min-w-0">
              <div className="font-display text-white text-sm uppercase truncate">{it.title || it.name || it.label}</div>
              <div className="text-white/50 text-xs truncate">{it.short_desc || it.excerpt || ""}</div>
            </div>
            <button onClick={() => startEdit(it)} className="text-white/70 hover:text-[#FFCC00] font-mono-acc text-xs uppercase">Edytuj</button>
            <button onClick={() => remove(it.id)} className="text-red-400 hover:text-red-300"><Trash2 size={16}/></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-5 z-50">
          <div className="bg-[#2A2A2A] border border-[#3a3a3a] p-8 w-full max-w-2xl max-h-[90vh] overflow-auto">
            <h3 className="font-display text-white text-2xl uppercase mb-6">{editing === "new" ? "Nowy" : "Edycja"}</h3>
            <div className="space-y-4">
              {fields.map((f) => (
                <div key={f.key}>
                  <label className="font-mono-acc text-[10px] uppercase text-white/60">{f.label}</label>
                  {f.upload ? (
                    <div className="mt-2">
                      <ImageUrlField
                        value={draft[f.key]}
                        onChange={(v) => setDraft({ ...draft, [f.key]: v })}
                        headers={headers}
                      />
                    </div>
                  ) : f.type === "textarea" ? (
                    <textarea
                      rows={4}
                      value={draft[f.key] || ""}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2 outline-none focus:border-[#FFCC00]"
                    />
                  ) : f.type === "select" ? (
                    <select
                      value={draft[f.key] || ""}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2 outline-none focus:border-[#FFCC00]"
                    >
                      {(f.options || []).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={f.type || "text"}
                      value={draft[f.key] ?? ""}
                      onChange={(e) => setDraft({ ...draft, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                      className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2 outline-none focus:border-[#FFCC00]"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="px-5 py-3 border border-[#3a3a3a] text-white/70 font-mono-acc text-xs uppercase">Anuluj</button>
              <button onClick={save} className="bg-[#FFCC00] text-[#2A2A2A] font-display text-xs px-6 py-3 btn-brutal inline-flex items-center gap-2"><Save size={14}/> ZAPISZ</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- News editor with multiple images ---------- */
function NewsEditor() {
  const headers = useAuthHeaders();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ title: "", excerpt: "", content: "", image_url: "", date: "", size: "small", images: [] });
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const reload = () => api.get("/news").then((r) => setItems(r.data));
  useEffect(() => { reload(); }, []);

  const startNew = () => {
    setEditing("new");
    setDraft({ title: "", excerpt: "", content: "", image_url: "", date: new Date().toISOString().split("T")[0], size: "small", images: [] });
  };
  const startEdit = (it) => { setEditing(it.id); setDraft({ ...it, images: it.images || [] }); };

  const save = async () => {
    if (editing === "new") await api.post("/news", draft, { headers });
    else await api.put(`/news/${editing}`, draft, { headers });
    setEditing(null); reload();
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć aktualność?")) return;
    await api.delete(`/news/${id}`, { headers });
    reload();
  };

  const handleGalleryFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: r } = await api.post("/upload", fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      const newImgUrl = `${BACKEND_URL}${r.url}`;
      setDraft((prev) => ({ ...prev, images: [...prev.images, { image_url: newImgUrl, order: prev.images.length }] }));
    } catch (e2) { console.error(e2); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImg = (i) => setDraft({ ...draft, images: draft.images.filter((_, idx) => idx !== i) });

  return (
    <div data-testid="news-editor">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-white text-3xl uppercase">Aktualności</h2>
        <button onClick={startNew} className="bg-[#FFCC00] text-[#2A2A2A] font-display text-xs px-5 py-3 btn-brutal inline-flex items-center gap-2">
          <Plus size={14}/> NOWA AKTUALNOŚĆ
        </button>
      </div>

      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="bg-[#1F1F1F] border border-[#3a3a3a] p-4 flex items-center gap-4">
            <img src={it.image_url} alt="" className="w-20 h-14 object-cover"/>
            <div className="flex-1 min-w-0">
              <div className="font-display text-white text-sm uppercase truncate">{it.title}</div>
              <div className="text-white/50 text-xs truncate">{it.excerpt}</div>
            </div>
            <button onClick={() => startEdit(it)} className="text-white/70 hover:text-[#FFCC00] font-mono-acc text-xs uppercase">Edytuj</button>
            <button onClick={() => remove(it.id)} className="text-red-400 hover:text-red-300"><Trash2 size={16}/></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-5 z-50">
          <div className="bg-[#2A2A2A] border border-[#3a3a3a] p-8 w-full max-w-2xl max-h-[90vh] overflow-auto">
            <h3 className="font-display text-white text-2xl uppercase mb-6">{editing === "new" ? "Nowa aktualność" : "Edycja"}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="font-mono-acc text-[10px] uppercase text-white/60">Tytuł</label>
                <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2 outline-none focus:border-[#FFCC00]"/>
              </div>
              <div>
                <label className="font-mono-acc text-[10px] uppercase text-white/60">Skrót (na listę)</label>
                <textarea rows={2} value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
                  className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2 outline-none focus:border-[#FFCC00]"/>
              </div>
              <div>
                <label className="font-mono-acc text-[10px] uppercase text-white/60">Pełna treść</label>
                <textarea rows={6} value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                  className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2 outline-none focus:border-[#FFCC00]"/>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-mono-acc text-[10px] uppercase text-white/60">Data</label>
                  <input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                    className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2 outline-none focus:border-[#FFCC00]"/>
                </div>
                <div>
                  <label className="font-mono-acc text-[10px] uppercase text-white/60">Rozmiar kafla</label>
                  <select value={draft.size} onChange={(e) => setDraft({ ...draft, size: e.target.value })}
                    className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2 outline-none focus:border-[#FFCC00]">
                    <option value="small">Mały</option>
                    <option value="wide">Szeroki</option>
                    <option value="tall">Wysoki</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-mono-acc text-[10px] uppercase text-white/60">Główne zdjęcie (URL)</label>
                <ImageUrlField value={draft.image_url} onChange={(v) => setDraft({ ...draft, image_url: v })} headers={headers}/>
              </div>

              <div>
                <label className="font-display text-white text-lg uppercase block mb-3">Galeria zdjęć</label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {draft.images.map((img, i) => (
                    <div key={i} className="relative aspect-square bg-black border border-[#3a3a3a]">
                      <img src={img.image_url} alt="" className="w-full h-full object-cover"/>
                      <button onClick={() => removeImg(i)} className="absolute top-1 right-1 bg-red-500 text-white p-1"><Trash2 size={10}/></button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="border border-[#FFCC00] text-[#FFCC00] font-mono-acc text-[10px] uppercase px-4 py-2 inline-flex items-center gap-2">
                  <Upload size={14}/> {uploading ? "Wgrywanie..." : "DODAJ ZDJĘCIE DO GALERII"}
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleGalleryFile} className="hidden"/>
              </div>
            </div>

            <div className="mt-8 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="px-5 py-3 border border-[#3a3a3a] text-white/70 font-mono-acc text-xs uppercase">Anuluj</button>
              <button onClick={save} className="bg-[#FFCC00] text-[#2A2A2A] font-display text-xs px-6 py-3 btn-brutal inline-flex items-center gap-2"><Save size={14}/> ZAPISZ</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Access editor ---------- */
function AccessEditor() {
  const headers = useAuthHeaders();
  const [data, setData] = useState({ description: "", pricing: [], bank_account: "", blik_phone: "", payment_info: "" });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/access").then((r) => setData({
      description: r.data.description || "",
      pricing: r.data.pricing || [],
      bank_account: r.data.bank_account || "",
      blik_phone: r.data.blik_phone || "",
      payment_info: r.data.payment_info || "",
    }));
  }, []);

  const setRow = (i, field, value) => {
    const next = [...data.pricing];
    next[i] = { ...next[i], [field]: value };
    setData({ ...data, pricing: next });
  };

  const save = async () => {
    await api.put("/access", data, { headers });
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  while (data.pricing.length < 6) data.pricing.push({ label: "", price: "" });

  return (
    <div data-testid="access-editor">
      <h2 className="font-display text-white text-3xl uppercase mb-6">Cennik / Dostęp</h2>

      <label className="font-mono-acc text-[10px] uppercase text-white/60">Opis</label>
      <textarea
        rows={6} value={data.description}
        onChange={(e) => setData({ ...data, description: e.target.value })}
        className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2 outline-none focus:border-[#FFCC00]"
      />

      <h3 className="font-display text-white text-xl uppercase mt-8 mb-3">Pozycje cennika (6)</h3>
      <div className="space-y-2">
        {data.pricing.slice(0, 6).map((p, i) => (
          <div key={i} className="grid grid-cols-2 gap-3">
            <input value={p.label} onChange={(e) => setRow(i, "label", e.target.value)} placeholder="Nazwa pakietu" className="bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>
            <input value={p.price} onChange={(e) => setRow(i, "price", e.target.value)} placeholder="Cena" className="bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>
          </div>
        ))}
      </div>

      <h3 className="font-display text-white text-xl uppercase mt-8 mb-3">Dane do płatności</h3>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="font-mono-acc text-[10px] uppercase text-white/60">Numer konta bankowego</label>
          <input value={data.bank_account} onChange={(e) => setData({ ...data, bank_account: e.target.value })}
            className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>
        </div>
        <div>
          <label className="font-mono-acc text-[10px] uppercase text-white/60">Telefon BLIK</label>
          <input value={data.blik_phone} onChange={(e) => setData({ ...data, blik_phone: e.target.value })}
            className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>
        </div>
        <div className="md:col-span-2">
          <label className="font-mono-acc text-[10px] uppercase text-white/60">Informacja o płatności</label>
          <textarea rows={3} value={data.payment_info} onChange={(e) => setData({ ...data, payment_info: e.target.value })}
            className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>
        </div>
      </div>

      <button onClick={save} className="mt-6 bg-[#FFCC00] text-[#2A2A2A] font-display text-xs px-6 py-3 btn-brutal inline-flex items-center gap-2">
        <Save size={14}/> ZAPISZ
      </button>
      {saved && <span className="ml-3 text-[#FFCC00] font-mono-acc text-xs">Zapisano ✓</span>}
    </div>
  );
}

/* ---------- About editor with image upload ---------- */
function AboutEditor() {
  const headers = useAuthHeaders();
  const [data, setData] = useState({ description: "", gallery: [], stats: [] });
  const [newImg, setNewImg] = useState("");
  const [saved, setSaved] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/about").then((r) => setData({
      description: r.data.description || "",
      gallery: r.data.gallery || [],
      stats: r.data.stats || [],
    }));
  }, []);

  const addImg = () => { if (newImg) { setData({ ...data, gallery: [...data.gallery, newImg] }); setNewImg(""); } };
  const removeImg = (i) => setData({ ...data, gallery: data.gallery.filter((_, idx) => idx !== i) });

  const setStat = (i, k, v) => {
    const next = [...data.stats];
    next[i] = { ...next[i], [k]: v };
    setData({ ...data, stats: next });
  };
  const addStat = () => setData({ ...data, stats: [...data.stats, { value: "", label: "" }] });
  const removeStat = (i) => setData({ ...data, stats: data.stats.filter((_, idx) => idx !== i) });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: r } = await api.post("/upload", fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setData((prev) => ({ ...prev, gallery: [...prev.gallery, `${BACKEND_URL}${r.url}`] }));
    } catch (e2) { console.error(e2); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => { await api.put("/about", data, { headers }); setSaved(true); setTimeout(() => setSaved(false), 2000); };

  return (
    <div data-testid="about-editor">
      <h2 className="font-display text-white text-3xl uppercase mb-6">O nas</h2>
      <label className="font-mono-acc text-[10px] uppercase text-white/60">Opis</label>
      <textarea rows={6} value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })}
        className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2 outline-none focus:border-[#FFCC00]"/>

      <h3 className="font-display text-white text-xl uppercase mt-8 mb-3">Statystyki / liczby</h3>
      <div className="space-y-2" data-testid="stats-editor">
        {data.stats.map((s, i) => (
          <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-3 items-center">
            <input value={s.value} onChange={(e) => setStat(i, "value", e.target.value)} placeholder="Wartość (np. 2018)" className="bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>
            <input value={s.label} onChange={(e) => setStat(i, "label", e.target.value)} placeholder="Etykieta (np. Rok założenia)" className="bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>
            <button onClick={() => removeStat(i)} className="text-red-400 p-2"><Trash2 size={14}/></button>
          </div>
        ))}
        <button onClick={addStat} className="mt-2 border border-[#FFCC00] text-[#FFCC00] font-mono-acc text-xs uppercase px-4 py-2 inline-flex items-center gap-2">
          <Plus size={12}/> Dodaj statystykę
        </button>
      </div>

      <h3 className="font-display text-white text-xl uppercase mt-8 mb-3">Galeria</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {data.gallery.map((src, i) => (
          <div key={i} className="relative aspect-square bg-[#1F1F1F] border border-[#3a3a3a] overflow-hidden">
            <img src={src} alt="" className="w-full h-full object-cover"/>
            <button onClick={() => removeImg(i)} className="absolute top-1 right-1 bg-red-500 text-white p-1"><Trash2 size={12}/></button>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <input value={newImg} onChange={(e) => setNewImg(e.target.value)} placeholder="URL nowego zdjęcia" className="flex-1 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>
        <button onClick={addImg} className="bg-[#FFCC00] text-[#2A2A2A] font-display text-xs px-4 py-2 btn-brutal">DODAJ URL</button>
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="border border-[#FFCC00] text-[#FFCC00] font-mono-acc text-xs px-4 inline-flex items-center gap-1.5 disabled:opacity-50">
          <Upload size={14}/> {uploading ? "..." : "Wgraj plik"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden"/>
      </div>

      <button onClick={save} className="mt-6 bg-[#FFCC00] text-[#2A2A2A] font-display text-xs px-6 py-3 btn-brutal inline-flex items-center gap-2">
        <Save size={14}/> ZAPISZ
      </button>
      {saved && <span className="ml-3 text-[#FFCC00] font-mono-acc text-xs">Zapisano ✓</span>}
    </div>
  );
}

/* ---------- Contact editor ---------- */
function ContactEditor() {
  const headers = useAuthHeaders();
  const [data, setData] = useState({ address: "", phone: "", email: "", description: "", map_url: "", hours: [] });
  const [saved, setSaved] = useState(false);

  useEffect(() => { api.get("/contact").then((r) => setData({ ...r.data, hours: r.data.hours || [] })); }, []);

  const setHour = (i, k, v) => {
    const next = [...data.hours];
    next[i] = { ...next[i], [k]: v };
    setData({ ...data, hours: next });
  };
  const addHour = () => setData({ ...data, hours: [...data.hours, { day: "", time: "" }] });
  const removeHour = (i) => setData({ ...data, hours: data.hours.filter((_, idx) => idx !== i) });

  const save = async () => { await api.put("/contact", data, { headers }); setSaved(true); setTimeout(() => setSaved(false), 2000); };

  return (
    <div data-testid="contact-editor">
      <h2 className="font-display text-white text-3xl uppercase mb-6">Kontakt</h2>
      <p className="text-white/55 text-sm mb-6">Edycja danych kontaktowych zmienia również informacje wyświetlane w stopce strony.</p>
      <div className="space-y-4 max-w-2xl">
        {[
          ["address", "Adres"],
          ["phone", "Telefony (jeden pod drugim)"],
          ["email", "Email"],
          ["description", "Opis"],
          ["map_url", "URL Google Maps embed"],
        ].map(([k, l]) => (
          <div key={k}>
            <label className="font-mono-acc text-[10px] uppercase text-white/60">{l}</label>
            {k === "description" || k === "phone" ? (
              <textarea rows={k === "phone" ? 2 : 3} value={data[k] || ""} onChange={(e) => setData({ ...data, [k]: e.target.value })} className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2 outline-none focus:border-[#FFCC00]"/>
            ) : (
              <input value={data[k] || ""} onChange={(e) => setData({ ...data, [k]: e.target.value })} className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2 outline-none focus:border-[#FFCC00]"/>
            )}
          </div>
        ))}
      </div>

      <h3 className="font-display text-white text-xl uppercase mt-8 mb-3">Godziny otwarcia</h3>
      <div className="space-y-2 max-w-2xl" data-testid="hours-editor">
        {data.hours.map((h, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center">
            <input value={h.day} onChange={(e) => setHour(i, "day", e.target.value)} placeholder="Dzień (np. Wtorek)" className="bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>
            <input value={h.time} onChange={(e) => setHour(i, "time", e.target.value)} placeholder="Godziny (np. 17:00 – 20:00)" className="bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>
            <button onClick={() => removeHour(i)} className="text-red-400 p-2"><Trash2 size={14}/></button>
          </div>
        ))}
        <button onClick={addHour} className="mt-2 border border-[#FFCC00] text-[#FFCC00] font-mono-acc text-xs uppercase px-4 py-2 inline-flex items-center gap-2">
          <Plus size={12}/> Dodaj wiersz
        </button>
      </div>

      <button onClick={save} className="mt-8 bg-[#FFCC00] text-[#2A2A2A] font-display text-xs px-6 py-3 btn-brutal inline-flex items-center gap-2">
        <Save size={14}/> ZAPISZ
      </button>
      {saved && <span className="ml-3 text-[#FFCC00] font-mono-acc text-xs">Zapisano ✓</span>}
    </div>
  );
}

/* ---------- Documents editor ---------- */
function DocumentsEditor() {
  const headers = useAuthHeaders();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ title: "", file_url: "", description: "", order: 0 });
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const reload = () => api.get("/documents").then((r) => setItems(r.data));
  useEffect(() => { reload(); }, []);

  const startNew = () => { setEditing("new"); setDraft({ title: "", file_url: "", description: "", order: items.length + 1 }); };
  const startEdit = (it) => { setEditing(it.id); setDraft({ ...it }); };
  const save = async () => {
    if (editing === "new") await api.post("/documents", draft, { headers });
    else await api.put(`/documents/${editing}`, draft, { headers });
    setEditing(null); reload();
  };
  const remove = async (id) => {
    if (!window.confirm("Usunąć dokument?")) return;
    await api.delete(`/documents/${id}`, { headers });
    reload();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: r } = await api.post("/upload-doc", fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setDraft((prev) => ({
        ...prev,
        file_url: r.url,
        title: prev.title || (r.original_name || "").replace(/\.[^.]+$/, ""),
      }));
    } catch (e2) { console.error(e2); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div data-testid="documents-editor">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-white text-3xl uppercase">Regulamin / Pliki do pobrania</h2>
        <button onClick={startNew} className="bg-[#FFCC00] text-[#2A2A2A] font-display text-xs px-5 py-3 btn-brutal inline-flex items-center gap-2">
          <Plus size={14}/> NOWY DOKUMENT
        </button>
      </div>

      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="bg-[#1F1F1F] border border-[#3a3a3a] p-4 flex items-center gap-4">
            <FileText size={28} className="text-[#FFCC00] flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="font-display text-white text-sm uppercase truncate">{it.title}</div>
              <div className="text-white/50 text-xs truncate font-mono-acc">{it.file_url}</div>
            </div>
            <button onClick={() => startEdit(it)} className="text-white/70 hover:text-[#FFCC00] font-mono-acc text-xs uppercase">Edytuj</button>
            <button onClick={() => remove(it.id)} className="text-red-400 hover:text-red-300"><Trash2 size={16}/></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-5 z-50">
          <div className="bg-[#2A2A2A] border border-[#3a3a3a] p-8 w-full max-w-2xl">
            <h3 className="font-display text-white text-2xl uppercase mb-6">{editing === "new" ? "Nowy dokument" : "Edycja dokumentu"}</h3>

            <label className="font-mono-acc text-[10px] uppercase text-white/60">Tytuł</label>
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="w-full mt-2 mb-4 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>

            <label className="font-mono-acc text-[10px] uppercase text-white/60">Opis</label>
            <textarea rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              className="w-full mt-2 mb-4 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>

            <label className="font-mono-acc text-[10px] uppercase text-white/60">Plik (URL lub wgraj)</label>
            <div className="flex gap-2 mt-2">
              <input value={draft.file_url} onChange={(e) => setDraft({ ...draft, file_url: e.target.value })}
                placeholder="/api/static/uploads/... lub https://..."
                className="flex-1 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="bg-[#FFCC00] text-[#2A2A2A] font-mono-acc text-[10px] uppercase px-4 inline-flex items-center gap-1.5 btn-brutal disabled:opacity-50">
                <Upload size={14}/> {uploading ? "..." : "Plik"}
              </button>
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.odt,.txt" onChange={handleFile} className="hidden"/>
            </div>
            <p className="text-white/40 text-xs mt-1">Akceptowane: PDF, DOC, DOCX, ODT, TXT (max 25 MB)</p>

            <label className="font-mono-acc text-[10px] uppercase text-white/60 block mt-4">Kolejność</label>
            <input type="number" value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })}
              className="w-full mt-2 bg-[#1F1F1F] border border-[#3a3a3a] text-white px-3 py-2"/>

            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="px-5 py-3 border border-[#3a3a3a] text-white/70 font-mono-acc text-xs uppercase">Anuluj</button>
              <button onClick={save} className="bg-[#FFCC00] text-[#2A2A2A] font-display text-xs px-6 py-3 btn-brutal inline-flex items-center gap-2"><Save size={14}/> ZAPISZ</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Signups list ---------- */
function SignupsList() {
  const headers = useAuthHeaders();
  const [items, setItems] = useState([]);
  const reload = () => api.get("/signups", { headers }).then((r) => setItems(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const markHandled = async (id) => {
    await api.put(`/signups/handled/${id}`, {}, { headers });
    reload();
  };
  const remove = async (id) => {
    if (!window.confirm("Usunąć zgłoszenie?")) return;
    await api.delete(`/signups/${id}`, { headers });
    reload();
  };

  return (
    <div data-testid="signups-list">
      <h2 className="font-display text-white text-3xl uppercase mb-6">Zgłoszenia na warsztaty</h2>
      {items.length === 0 ? (
        <p className="text-white/50">Brak zgłoszeń.</p>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} className={`bg-[#1F1F1F] border ${s.handled ? "border-[#3a3a3a] opacity-60" : "border-[#FFCC00]/50"} p-5`} data-testid={`signup-row-${s.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-mono-acc text-[10px] uppercase text-[#FFCC00] mb-1">
                    {s.workshop_title} · {new Date(s.created_at).toLocaleString("pl-PL")}
                  </div>
                  <div className="font-display text-white text-lg uppercase">{s.name}</div>
                  <div className="mt-2 text-sm text-white/75 space-y-1">
                    <div>📧 <a href={`mailto:${s.email}`} className="text-[#FFCC00] hover:underline">{s.email}</a></div>
                    {s.phone && <div>📞 <a href={`tel:${s.phone}`} className="text-[#FFCC00] hover:underline">{s.phone}</a></div>}
                    {s.message && <div className="mt-2 italic text-white/65 border-l-2 border-[#3a3a3a] pl-3">"{s.message}"</div>}
                  </div>
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  {!s.handled && (
                    <button onClick={() => markHandled(s.id)} className="bg-[#FFCC00] text-[#2A2A2A] font-mono-acc text-[10px] uppercase px-3 py-2 inline-flex items-center gap-1.5">
                      <CheckCircle2 size={12}/> Obsłużone
                    </button>
                  )}
                  <button onClick={() => remove(s.id)} className="text-red-400 hover:text-red-300 inline-flex items-center justify-center p-2">
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ---------- Participants & Check-ins admin ---------- */
function ParticipantsAdmin() {
  const headers = useAuthHeaders();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "live";
  const [participants, setParticipants] = useState([]);
  const [checkins, setCheckins] = useState({ items: [], count: 0 });
  const [period, setPeriod] = useState("today"); // today | month | custom
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState(initialTab); // live | list

  const loadParticipants = async () => {
    try {
      const url = `/admin/participants?month=${month}&year=${year}`;
      const { data } = await api.get(url, { headers });
      setParticipants(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load participants:", e);
      setParticipants([]);
    }
  };
  const loadCheckins = async () => {
    try {
      let url = `/admin/checkins?period=${period}`;
      if (period === "custom") {
        url = `/admin/checkins?month=${month}&year=${year}`;
      }
      const { data } = await api.get(url, { headers });
      setCheckins(data && data.items ? data : { items: [], count: 0 });
    } catch (e) {
      console.error("Failed to load checkins:", e);
      setCheckins({ items: [], count: 0 });
    }
  };

  useEffect(() => { loadParticipants(); /* eslint-disable-next-line */ }, [month, year]);
  useEffect(() => { loadCheckins(); /* eslint-disable-next-line */ }, [period, month, year]);

  // Live polling for check-ins (every 10s while on live tab and not custom period)
  useEffect(() => {
    if (tab !== "live" || period === "custom") return;
    const t = setInterval(loadCheckins, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [tab, period]);

  const removeParticipant = async (p) => {
    const msg = p.parent_id 
      ? `Usunąć konto dziecka: ${p.first_name} ${p.last_name}?` 
      : `Usunąć uczestnika ${p.first_name} ${p.last_name} i wszystkie powiązane konta dziecięce oraz historię wejść?`;
      
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/participants/${p.id}`, { headers });
      loadParticipants();
      loadCheckins();
    } catch (e) {
      alert("Błąd podczas usuwania");
    }
  };

  const filtered = Array.isArray(participants) ? participants.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [p.first_name, p.last_name, p.email, p.city].some((v) => (v || "").toLowerCase().includes(q));
  }) : [];

  return (
    <div data-testid="participants-admin">
      <h2 className="font-display text-white text-3xl uppercase mb-2">Uczestnicy / Wejścia</h2>
      <p className="text-white/55 text-sm mb-6">Baza uczestników kiosku „Wejście" oraz historia ich obecności w pracowni.</p>

      <div className="flex gap-2 mb-6 border-b border-[#3a3a3a]">
        <button
          onClick={() => setTab("live")}
          data-testid="tab-live"
          className={`font-mono-acc text-xs uppercase px-5 py-3 border-b-2 transition-colors ${tab === "live" ? "border-[#FFCC00] text-[#FFCC00]" : "border-transparent text-white/55 hover:text-white"}`}
        >
          Wejścia na żywo
        </button>
        <button
          onClick={() => setTab("list")}
          data-testid="tab-list"
          className={`font-mono-acc text-xs uppercase px-5 py-3 border-b-2 transition-colors ${tab === "list" ? "border-[#FFCC00] text-[#FFCC00]" : "border-transparent text-white/55 hover:text-white"}`}
        >
          Baza uczestników ({participants.length})
        </button>
      </div>

      {tab === "live" && (
        <div data-testid="checkins-panel">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
            <div>
              <div className="kicker">Okres</div>
              <div className="flex flex-wrap gap-2 mt-2">
                <PeriodBtn active={period === "today"} onClick={() => setPeriod("today")}>Dziś</PeriodBtn>
                <PeriodBtn active={period === "month"} onClick={() => setPeriod("month")}>Bieżący miesiąc</PeriodBtn>
                <PeriodBtn active={period === "custom"} onClick={() => setPeriod("custom")}>Wybierz datę</PeriodBtn>
                
                {period === "custom" && (
                  <div className="flex gap-2 ml-4">
                    <select value={month} onChange={(e) => setMonth(e.target.value)} className="bg-[#1F1F1F] border border-[#3a3a3a] text-white text-xs px-2 py-1">
                      {Array.from({length: 12}, (_, i) => (
                        <option key={i+1} value={i+1}>{new Date(2000, i).toLocaleString('pl', {month: 'long'})}</option>
                      ))}
                    </select>
                    <select value={year} onChange={(e) => setYear(e.target.value)} className="bg-[#1F1F1F] border border-[#3a3a3a] text-white text-xs px-2 py-1">
                      {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-white text-5xl text-[#FFCC00]" data-testid="checkins-count">{checkins.count}</div>
              <div className="font-mono-acc text-[10px] uppercase text-white/55">
                {period === "today" ? "wejść dzisiaj" : (period === "month" ? "wejść w miesiącu" : "wejść w wybranym okresie")}
              </div>
            </div>
          </div>

          <div className="bg-[#1F1F1F] border border-[#3a3a3a] divide-y divide-[#3a3a3a]" data-testid="checkins-list">
            {checkins.items.length === 0 && (
              <div className="p-6 text-white/45 text-sm">Brak rejestracji wejść w wybranym okresie.</div>
            )}
            {checkins.items.map((c) => (
              <div key={c.id} className="p-4 flex items-center justify-between gap-4">
                <div>
                  <div className="font-display text-white uppercase text-sm">{c.name_snapshot || "—"}</div>
                  <div className="font-mono-acc text-[10px] text-white/45 uppercase">
                    {new Date(c.created_at).toLocaleString("pl-PL")}
                    {c.parent_id ? " · konto dziecka" : " · konto główne"}
                  </div>
                </div>
                <div className="font-mono-acc text-[10px] text-[#FFCC00]">✓ ZAREJESTROWANO</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "list" && (
        <div>
          <div className="flex items-center justify-between mb-5 flex-wrap gap-4">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj po imieniu, nazwisku, emailu, mieście..."
              className="flex-1 min-w-[200px] bg-[#1F1F1F] border border-[#3a3a3a] text-white px-4 py-3 outline-none focus:border-[#FFCC00]"
              data-testid="participants-search"
            />
            <div className="flex gap-2">
              <select value={month} onChange={(e) => setMonth(e.target.value)} className="bg-[#1F1F1F] border border-[#3a3a3a] text-white text-xs px-3 py-2">
                {Array.from({length: 12}, (_, i) => (
                  <option key={i+1} value={i+1}>{new Date(2000, i).toLocaleString('pl', {month: 'long'})}</option>
                ))}
              </select>
              <select value={year} onChange={(e) => setYear(e.target.value)} className="bg-[#1F1F1F] border border-[#3a3a3a] text-white text-xs px-3 py-2">
                {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto bg-[#1F1F1F] border border-[#3a3a3a]">
            <table className="w-full text-sm" data-testid="participants-table">
              <thead className="bg-[#2A2A2A] text-white/55 text-left">
                <tr>
                  <Th>Imię i nazwisko</Th>
                  <Th className="hidden sm:table-cell">Typ</Th>
                  <Th className="hidden lg:table-cell">Email</Th>
                  <Th className="hidden md:table-cell">Data ur.</Th>
                  <Th className="hidden sm:table-cell">Miasto</Th>
                  <Th align="right">Wejścia / mc</Th>
                  <Th align="right" className="hidden xl:table-cell">Wejścia łącznie</Th>
                  <Th align="right" className="hidden xl:table-cell">Marketing</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="p-6 text-white/45 text-center">Brak uczestników.</td></tr>
                )}
                {filtered.map((p) => (
                  <tr key={p.id} className="border-t border-[#3a3a3a] hover:bg-[#2A2A2A]" data-testid={`participant-row-${p.id}`}>
                    <Td>
                      <div className="font-display text-white uppercase text-xs md:text-sm">{p.first_name} {p.last_name}</div>
                      {p.is_minor && <div className="font-mono-acc text-[8px] md:text-[9px] text-orange-400 uppercase">Niepełnoletni</div>}
                    </Td>
                    <Td className="hidden sm:table-cell">
                      <span className={`font-mono-acc text-[9px] md:text-[10px] uppercase px-2 py-1 ${p.parent_id ? "bg-[#3a3a3a] text-white/70" : "bg-[#FFCC00] text-[#2A2A2A]"}`}>
                        {p.parent_id ? "Dziecko" : "Dorosły"}
                      </span>
                    </Td>
                    <Td className="hidden lg:table-cell"><span className="text-white/70 break-all">{p.email && p.email.endsWith("@local.invalid") ? "—" : p.email}</span></Td>
                    <Td className="hidden md:table-cell"><span className="font-mono-acc text-white/70">{p.dob}</span></Td>
                    <Td className="hidden sm:table-cell"><span className="text-white/70">{p.city || "—"}</span></Td>
                    <Td align="right"><span className="font-display text-[#FFCC00] text-base md:text-lg">{p.visits_this_month}</span></Td>
                    <Td align="right" className="hidden xl:table-cell"><span className="font-mono-acc text-white/70">{p.visits_total}</span></Td>
                    <Td align="right" className="hidden xl:table-cell">{p.marketing_consent ? "✓" : "—"}</Td>
                    <Td align="right">
                      <div className="flex items-center justify-end gap-2 md:gap-4">
                        <Link 
                          to={`/admin/participants/${p.parent_id || p.id}${p.parent_id ? `?child=${p.id}` : ""}`} 
                          className="text-[#FFCC00] hover:text-white transition-colors p-1" 
                          title="Szczegóły"
                        >
                          <Eye size={18}/>
                        </Link>
                        <button onClick={() => removeParticipant(p)} className="text-red-400 hover:text-red-300 transition-colors p-1 hidden md:block" title="Usuń">
                          <Trash2 size={16}/>
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminParticipantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const childId = searchParams.get("child");
  const { token } = useAuth();
  const [participant, setParticipant] = useState(null);
  const [targetId, setTargetId] = useState(childId || id);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/admin/participants/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setParticipant(data);
        if (!childId) setTargetId(data.id);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    if (id && token) load();
  }, [id, token, childId]);

  const removeParticipant = async () => {
    const p = all.find(a => a.id === targetId);
    if (!p) return;
    
    const msg = p.parent_id 
      ? `Usunąć konto dziecka: ${p.first_name} ${p.last_name}?` 
      : `Usunąć uczestnika ${p.first_name} ${p.last_name} i wszystkie powiązane konta dziecięce oraz historię wejść?`;
      
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/participants/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (p.id === id) navigate("/admin/participants?tab=list");
      else {
        // Refresh if it was a child
        const { data } = await api.get(`/admin/participants/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setParticipant(data);
        setTargetId(data.id);
      }
    } catch (e) {
      alert("Błąd podczas usuwania");
    }
  };

  const handleTotalLoad = useCallback((total) => {
    setParticipant(prev => {
      if (!prev) return prev;
      if (prev.id === targetId) {
        if (prev.visits_total === total) return prev;
        return { ...prev, visits_total: total };
      }
      const hasChanged = (prev.children || []).some(c => c.id === targetId && c.visits_total !== total);
      if (!hasChanged) return prev;
      const nextChildren = (prev.children || []).map(c => 
        c.id === targetId ? { ...c, visits_total: total } : c
      );
      return { ...prev, children: nextChildren };
    });
  }, [targetId]);

  if (loading) return <div className="text-white/50 font-mono-acc uppercase">Pobieranie danych...</div>;
  if (!participant) return <div className="text-red-400">Nie znaleziono uczestnika.</div>;

  const all = [
    { ...participant, label: `${participant.first_name} ${participant.last_name} (Profil główny)` },
    ...(participant.children || []).map(c => ({ ...c, label: `${c.first_name} ${c.last_name} (dziecko)` }))
  ];

  const current = all.find(a => a.id === targetId) || participant;

  return (
    <div data-testid="admin-participant-detail">
      <div className="flex items-center gap-4 mb-8">
        <Link to="/admin/participants?tab=list" className="text-white/50 hover:text-white transition-colors">
          <ArrowLeft size={24}/>
        </Link>
        <div>
          <div className="kicker">Szczegóły uczestnika</div>
          <h1 className="font-display text-white text-2xl md:text-3xl uppercase">
            {current.first_name} <span className="text-[#FFCC00]">{current.last_name}</span>
          </h1>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1 space-y-6">
          <div className="bg-[#1F1F1F] border border-[#3a3a3a] p-6">
            <h3 className="font-display text-white text-lg uppercase mb-4 border-b border-[#3a3a3a] pb-2">Informacje</h3>
            <div className="space-y-3">
              <DetailRow label="Email" value={current.email && current.email.endsWith("@local.invalid") ? "—" : current.email}/>
              <DetailRow label="Miasto" value={current.city}/>
              <DetailRow label="Data urodzenia" value={current.dob}/>
              <div className="grid grid-cols-2 gap-4">
                <DetailRow label="Suma wejść" value={current.visits_total} highlight/>
                <DetailRow label="Marketing" value={current.marketing_consent ? "TAK" : "NIE"}/>
              </div>
              <div className="pt-4 border-t border-[#3a3a3a]">
                <button 
                  onClick={removeParticipant}
                  className="w-full flex items-center justify-center gap-2 py-3 border border-red-500/30 text-red-400 hover:bg-red-500 hover:text-white transition-colors font-mono-acc text-[10px] uppercase"
                >
                  <Trash2 size={14}/> Usuń konto
                </button>
              </div>
            </div>
          </div>

          <div className="bg-[#1F1F1F] border border-[#3a3a3a] p-6">
            <h3 className="font-display text-white text-lg uppercase mb-4 border-b border-[#3a3a3a] pb-2">Powiązani uczestnicy</h3>
            <div className="space-y-2">
              {all.map(a => (
                <button
                  key={a.id}
                  onClick={() => setTargetId(a.id)}
                  className={`w-full text-left p-3 border transition-colors ${targetId === a.id ? "border-[#FFCC00] bg-[#FFCC00]/5" : "border-[#3a3a3a] hover:border-white/40"}`}
                >
                  <div className="font-display text-white text-xs uppercase">{a.first_name} {a.last_name}</div>
                  <div className="font-mono-acc text-[9px] text-white/40 uppercase">{a.parent_id ? "Dziecko" : "Główny profil"}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="bg-[#1F1F1F] border border-[#3a3a3a] p-4 md:p-8">
            <AttendanceSectionAdmin 
              participantId={targetId} 
              token={token} 
              onTotalLoad={handleTotalLoad}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, highlight = false }) {
  return (
    <div>
      <div className="font-mono-acc text-[9px] uppercase text-white/40">{label}</div>
      <div className={`text-sm truncate ${highlight ? "text-[#FFCC00] font-display uppercase text-lg" : "text-white"}`}>
        {value === null || value === undefined || value === "" ? "—" : value}
      </div>
    </div>
  );
}

function AttendanceSectionAdmin({ participantId, token, onTotalLoad }) {
  const [data, setData] = useState({ total: 0, history: [] });
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  const fetchAttendance = useCallback(async () => {
    if (!participantId || !token) return;
    setLoading(true);
    try {
      const { data: res } = await api.get("/participants/attendance", {
        params: { participant_id: participantId, month, year },
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res);
      if (onTotalLoad) onTotalLoad(res.total);
    } catch (e) {
      console.error("Attendance fetch error:", e);
      setData({ total: 0, history: [] });
    } finally {
      setLoading(false);
    }
  }, [participantId, month, year, token, onTotalLoad]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  const months = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];
  const years = [];
  const curYear = new Date().getFullYear();
  for (let y = curYear; y >= 2024; y--) years.push(y);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">Historia obecności</div>
          <div className="text-white text-xl">
            Suma wizyt: <span className="text-[#FFCC00] font-display uppercase">{data.total}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <select 
            value={year} 
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="bg-[#2A2A2A] border border-[#3a3a3a] text-white text-[11px] px-3 py-2 outline-none focus:border-[#FFCC00] font-mono-acc uppercase"
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select 
            value={month} 
            onChange={(e) => setMonth(parseInt(e.target.value))}
            className="bg-[#2A2A2A] border border-[#3a3a3a] text-white text-[11px] px-3 py-2 outline-none focus:border-[#FFCC00] font-mono-acc uppercase"
          >
            {months.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-white/30 text-[10px] animate-pulse font-mono-acc uppercase">Pobieranie danych...</div>
      ) : (
        <div className="space-y-3">
           <div className="text-white/50 text-[10px] uppercase font-mono-acc">
             Wizyty w {months[month-1]} {year}: {data.history.length}
           </div>
           {data.history.length > 0 ? (
             <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {data.history.map((h, i) => (
                  <div key={i} className="bg-[#2A2A2A] border border-[#3a3a3a] p-3 flex items-center gap-3">
                    <Calendar size={14} className="text-[#FFCC00]"/>
                    <span className="font-mono-acc text-xs text-white/80">{h.date}</span>
                  </div>
                ))}
             </div>
           ) : (
             <div className="bg-[#2A2A2A]/50 border border-[#3a3a3a] border-dashed p-6 text-center text-white/30 text-xs italic">
               Brak zarejestrowanych wejść w tym okresie.
             </div>
           )}
        </div>
      )}
    </div>
  );
}

function PeriodBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`font-mono-acc text-xs uppercase px-4 py-2 ${active ? "bg-[#FFCC00] text-[#2A2A2A]" : "border border-[#3a3a3a] text-white/65 hover:border-white"}`}
    >
      {children}
    </button>
  );
}

function Th({ children, align, className = "" }) {
  return <th className={`px-4 py-3 font-mono-acc text-[10px] uppercase tracking-widest ${align === "right" ? "text-right" : "text-left"} ${className}`}>{children}</th>;
}

function Td({ children, align, className = "" }) {
  return <td className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"} ${className}`}>{children}</td>;
}
